import { useCharactersStore } from '@/features/shared/stores/characters.store'
import { Character } from '@storyverse/shared'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'
import { characterService } from '../service/character.service'

export const characterKeys = {
    all: ['characters'] as const,
    list: (projectId: string) => [...characterKeys.all, 'list', projectId] as const,
}

import { useEffect } from 'react'

export const useCharacters = (projectId: string) => {
    const setCharacters = useCharactersStore(state => state.setCharacters)

    const query = useQuery({
        queryKey: characterKeys.list(projectId),
        queryFn: () => characterService.getCharacters(projectId),
        enabled: !!projectId,
        staleTime: 1000 * 60 * 5, // 5 minutes
        gcTime: 1000 * 60 * 30, // 30 minutes
    })

    useEffect(() => {
        if (query.data) {
            setCharacters(query.data)
        }
    }, [query.data, setCharacters])

    return query
}

export const useSaveCharacterMutation = () => {
    const queryClient = useQueryClient()
    const addCharacter = useCharactersStore(state => state.addCharacter)
    const updateCharacter = useCharactersStore(state => state.updateCharacter)

    return useMutation({
        mutationFn: (character: Character) => characterService.saveCharacter(character),
        onSuccess: (data, variables) => {
            queryClient.invalidateQueries({ queryKey: characterKeys.all })
            if (variables.id) {
                updateCharacter(data)
                toast.success('Character updated')
            } else {
                addCharacter(data)
                toast.success('Character added')
            }
        },
        onError: () => {
            toast.error('Failed to save character')
        }
    })
}

export const useDeleteCharacterMutation = () => {
    const queryClient = useQueryClient()
    const removeCharacter = useCharactersStore(state => state.removeCharacter)

    return useMutation({
        mutationFn: (id: string) => characterService.deleteCharacter(id),
        onSuccess: (_, id) => {
            queryClient.invalidateQueries({ queryKey: characterKeys.all })
            removeCharacter(id)
            toast.success('Character deleted')
        },
        onError: () => {
            toast.error('Failed to delete character')
        }
    })
}
